import { useEffect, useRef, useState } from "react";
import {
  Column,
  DataGrid,
  FilterRow,
  HeaderFilter,
  MasterDetail,
  Scrolling,
  SearchPanel,
  Selection,
  Item,
  Toolbar,
} from "devextreme-react/data-grid";
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { atom, useRecoilValue } from "recoil";
import { CheckBox } from "devextreme-react";

import { calculateGridHeight, convertUTCTimeToLocal } from "../../util/Utils";
import CustomPermissionLevelReport from "./components/CustomPermissionLevelReport";
import { SnackbarMessage, useSnackbar } from "../../hooks";
import {
  GetAllSitesInfoDTO
} from "../../api-client-nswag/taxportal-client";
import {
  customPermissionClientApi
} from "../../api-client-nswag/taxportal-client-runtime";
import { envProperties } from "../../util/EnvironmentProperties";
import AppNameToggle from "../../cui/commonComponents/AppNameToggle";
import { AppName } from "../../constants";

type DatagridRefType = DataGrid;

export const getAppName = atom({
  key: "getAppName",
  default: "TP",
});

function CustomPermissions() {
  const dataGridRef = useRef<DatagridRefType>(null);
  const templateRef = useRef<any>();
  const { showSnackbar, showSuccessSnackbar } = useSnackbar();

  const expandedRowKeysRef = useRef<Array<string | number>>([]);
  const sitesAbortRef = useRef<AbortController | null>(null);
  const appName = useRecoilValue(getAppName);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [pageNumber] = useState(1);
  const [dropDown, setDropDown] = useState("");

  const [searchTerm, setSearchTermState] = useState(() => {
    try {
      return sessionStorage.getItem("customPermissionSearchTerm") || "";
    } catch {
      return "";
    }
  });
  const [isRefreshingPage, setIsRefreshingPage] = useState(false);

  const [customPermissionData, setCustomPermissionData] = useState<any>({
    pending: true,
    fulfilled: true,
    sites: [],
    totalCount: 0,
  });
  //const NEW_FLOW_OPTIONS = ["Run Custom Permission", "Retry"];
  const NEW_FLOW_OPTIONS = ["Create Custom Groups and Add Users",
    "Apply Folder Permissions", "Create IR views and add navigation",
    "Apply IR Permissions",
    "Create PS views and add navigation",
    "Apply PS Permissions",
    "Create K1 Tracker Views and add navigation",
    "Apply K1 Tracker Permissions",
    "Create Deliverable Views and add navigation",
    "Apply Deliverables Permissions",
    "Send Email for failures and successes",
    "Apply All Custom Permissions",
    "Show Verification Banner in Site"];

  const CS_NEW_FLOW_OPTIONS = [
    "Create Custom Groups and Add Users",
    "Apply Folder Permissions",
    "Send Email for failures and successes",
    "Apply All Custom Permissions",
    "Show Verification Banner in Site"
  ];

  useEffect(() => {
    if (shouldFetch) {
      fetchSites();
      setShouldFetch(false);
    }
  }, [pageNumber, shouldFetch]);

  useEffect(() => {
    setShouldFetch(true);
  }, [pageNumber, appName]);

  useEffect(() => () => sitesAbortRef.current?.abort(), []);

  const setSearchTerm = (value: string) => {
    setSearchTermState(value);
    try {
      if (value) {
        sessionStorage.setItem("customPermissionSearchTerm", value);
      } else {
        sessionStorage.removeItem("customPermissionSearchTerm");
      }
    } catch {
    }
  };

  const handleRefreshPage = async () => {
    if (isRefreshingPage) return;
    setIsRefreshingPage(true);

    const instance: any = dataGridRef.current?.instance;
    const expandedKeys: Array<string | number> =
      (expandedRowKeysRef.current ?? []).slice();

    try {
      await fetchSites();
      instance?.refresh?.();
      instance?.repaint?.();

      window.setTimeout(() => {
        expandedKeys.forEach((k) => {
          try {
            instance?.expandRow?.(k);
          } catch {
          }
        });
      }, 0);
    } finally {
      setIsRefreshingPage(false);
    }
  };

  const fetchSites = async () => {
    sitesAbortRef.current?.abort();
    const controller = new AbortController();
    sitesAbortRef.current = controller;

    try {
      setCustomPermissionData({
        pending: true,
        fulfilled: true,
        sites: [],
        totalCount: 0,
      });
      const data = await customPermissionClientApi.report_GetActiveSites(appName, controller.signal);
      if (controller.signal.aborted) return;
      const dataArray: any[] = Array.isArray(data) ? data : [];
      const sites = dataArray.map((item: any) => {
        return { ...item, isCustomPermissionApplied: item.isCustomPermissionApplied === true };
      });
      setCustomPermissionData({
        pending: false,
        fulfilled: true,
        sites,
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      setCustomPermissionData((prev: any) => ({
        ...prev,
        pending: false,
        sites: [],
      }));
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    }
  };

  const handleSelectedSiteProcess = async (selectedSite: GetAllSitesInfoDTO) => {
    try {
      if (dropDown === "Show Verification Banner in Site") {
        if (!selectedSite.hqSiteId) throw new Error("Site ID is required.");
        await customPermissionClientApi.updateLegalEntityPermissionsSchedule({
          siteId: selectedSite.hqSiteId,
          status: "VERIFICATION IN PROGRESS",
        });
        showSuccessSnackbar({ message: "Verification banner updated." as SnackbarMessage });
      }
      if (dropDown === "Apply All Custom Permissions") {
        showSnackbar({ message: "Apply All Custom Permissions triggered." as SnackbarMessage, severity: "info" });

        var CpResponse = await customPermissionClientApi.customPermission_Initiate(
          selectedSite.hqSiteId,
          selectedSite.mdmClientId!
        );
        if (CpResponse.success === true) {
          showSuccessSnackbar({ message: CpResponse.message as SnackbarMessage });
        } else {
          showSnackbar({
            message: CpResponse.message as SnackbarMessage,
            severity: "error",
          });
        }
      }
      if (dropDown === "Create Custom Groups and Add Users") {
        showSnackbar({ message: "Create Custom Groups and Add Users triggered." as SnackbarMessage, severity: "info" });

        var CpODResponse = await customPermissionClientApi.customPermission_OnDemand(selectedSite.hqSiteId, selectedSite.mdmClientId!, "CreateGroup", "All");
        if (CpODResponse.success === true) {
          showSuccessSnackbar({ message: CpODResponse.message as SnackbarMessage });
        } else {
          showSnackbar({
            message: CpODResponse.message as SnackbarMessage,
            severity: "error",
          });
        }
      }
      if (dropDown === "Apply Folder Permissions") {
        showSnackbar({ message: "Apply Folder Permissions triggered." as SnackbarMessage, severity: "info" });

        var CpFPResponse = await customPermissionClientApi.customPermission_OnDemand(selectedSite.hqSiteId, selectedSite.mdmClientId!, "UpdateFolderPermission", "All");
        if (CpFPResponse.success === true) {
          showSuccessSnackbar({ message: CpFPResponse.message as SnackbarMessage });
        } else {
          showSnackbar({
            message: CpFPResponse.message as SnackbarMessage,
            severity: "error",
          });
        }
      }
      if (dropDown === "Create IR views and add navigation") {
        showSnackbar({ message: "Create IR views and add navigation triggered." as SnackbarMessage, severity: "info" });

        var CpIRResponse = await customPermissionClientApi.customPermission_OnDemand(selectedSite.hqSiteId, selectedSite.mdmClientId!, "CreateView", "Information Request List");
        if (CpIRResponse.success === true) {
          showSuccessSnackbar({ message: CpIRResponse.message as SnackbarMessage });
        } else {
          showSnackbar({
            message: CpIRResponse.message as SnackbarMessage,
            severity: "error",
          });
        }
      }
      if (dropDown === "Create PS views and add navigation") {
        showSnackbar({ message: "Create PS views and add navigation triggered." as SnackbarMessage, severity: "info" });

        var CpPSResponse = await customPermissionClientApi.customPermission_OnDemand(selectedSite.hqSiteId, selectedSite.mdmClientId!, "CreateView", "Project Status");
        if (CpPSResponse.success === true) {
          showSuccessSnackbar({ message: CpPSResponse.message as SnackbarMessage });
        } else {
          showSnackbar({
            message: CpPSResponse.message as SnackbarMessage,
            severity: "error",
          });
        }
      }
      if (dropDown === "Create K1 Tracker Views and add navigation") {
        showSnackbar({ message: "Create K1 Tracker Views and add navigation triggered." as SnackbarMessage, severity: "info" });

        var CpK1Response = await customPermissionClientApi.customPermission_OnDemand(selectedSite.hqSiteId, selectedSite.mdmClientId!, "CreateView", "K-1 Tracker");
        if (CpK1Response.success === true) {
          showSuccessSnackbar({ message: CpK1Response.message as SnackbarMessage });
        } else {
          showSnackbar({
            message: CpK1Response.message as SnackbarMessage,
            severity: "error",
          });
        }
      }
      if (dropDown === "Create Deliverable Views and add navigation") {
        showSnackbar({ message: "Create Deliverable Views and add navigation triggered." as SnackbarMessage, severity: "info" });

        var CpDeliverablesResponse = await customPermissionClientApi.customPermission_OnDemand(selectedSite.hqSiteId, selectedSite.mdmClientId!, "CreateView", "Deliverables");
        if (CpDeliverablesResponse.success === true) {
          showSuccessSnackbar({ message: CpDeliverablesResponse.message as SnackbarMessage });
        } else {
          showSnackbar({
            message: CpDeliverablesResponse.message as SnackbarMessage,
            severity: "error",
          });
        }
      }
      if (dropDown === "Apply IR Permissions") {
        showSnackbar({ message: "Apply IR Permissions triggered." as SnackbarMessage, severity: "info" });

        var CpIrPermissionResponse = await customPermissionClientApi.customPermission_OnDemand(selectedSite.hqSiteId, selectedSite.mdmClientId!, "UpdateViewPermission", "Information Request List");
        if (CpIrPermissionResponse.success === true) {
          showSuccessSnackbar({ message: CpIrPermissionResponse.message as SnackbarMessage });
        } else {
          showSnackbar({
            message: CpIrPermissionResponse.message as SnackbarMessage,
            severity: "error",
          });
        }
      }
      if (dropDown === "Apply PS Permissions") {
        showSnackbar({ message: "Apply PS Permissions triggered." as SnackbarMessage, severity: "info" });

        var CpPsPermissionResponse = await customPermissionClientApi.customPermission_OnDemand(selectedSite.hqSiteId, selectedSite.mdmClientId!, "UpdateViewPermission", "Project Status");
        if (CpPsPermissionResponse.success === true) {
          showSuccessSnackbar({ message: CpPsPermissionResponse.message as SnackbarMessage });
        } else {
          showSnackbar({
            message: CpPsPermissionResponse.message as SnackbarMessage,
            severity: "error",
          });
        }
      }
      if (dropDown === "Apply K1 Tracker Permissions") {
        showSnackbar({ message: "Apply K1 Tracker Permissions triggered." as SnackbarMessage, severity: "info" });

        var CpK1TrackerPermissionResponse = await customPermissionClientApi.customPermission_OnDemand(selectedSite.hqSiteId, selectedSite.mdmClientId!, "UpdateViewPermission", "K-1 Tracker");
        if (CpK1TrackerPermissionResponse.success === true) {
          showSuccessSnackbar({ message: CpK1TrackerPermissionResponse.message as SnackbarMessage });
        } else {
          showSnackbar({
            message: CpK1TrackerPermissionResponse.message as SnackbarMessage,
            severity: "error",
          });
        }
      }
      if (dropDown === "Apply Deliverables Permissions") {
        showSnackbar({ message: "Apply Deliverables Permissions triggered." as SnackbarMessage, severity: "info" });

        var CpDeliverablesPermissionResponse = await customPermissionClientApi.customPermission_OnDemand(selectedSite.hqSiteId, selectedSite.mdmClientId!, "UpdateViewPermission", "Deliverables");
        if (CpDeliverablesPermissionResponse.success === true) {
          showSuccessSnackbar({ message: CpDeliverablesPermissionResponse.message as SnackbarMessage });
        } else {
          showSnackbar({
            message: CpDeliverablesPermissionResponse.message as SnackbarMessage,
            severity: "error",
          });
        }
      }
      if (dropDown === "Send Email for failures and successes") {
        showSnackbar({ message: "Send Email for failures and successes triggered." as SnackbarMessage, severity: "info" });

        var Emailresponse = await customPermissionClientApi.customPermission_EmailNotification(selectedSite.hqSiteId);
        if (Emailresponse.success === true) {
          showSuccessSnackbar({ message: Emailresponse.message as SnackbarMessage });
        } else {
          showSnackbar({
            message: Emailresponse.message as SnackbarMessage,
            severity: "error",
          });
        }
      }
      dataGridRef.current!.instance.clearSelection();
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    }
  };

  const cellRenderForStatus = (e: any) => {
    return <div title={e.data.message}>{e.data.status}</div>;
  };

  const handleChange = (e: SelectChangeEvent) => {
    const val = e.target.value;
    setDropDown(val);
  };

  const renderActionsColumn = ({ row: { data } }: any) => {
    return data.status ? (
      <Button
        variant="outlined"
        sx={{
          minWidth: "40px",
          padding: "2px 2px",
          height: "24px",
          fontSize: "12px",
          textAlign: "center",
        }}
        onClick={() => handleSelectedSiteProcess(data)}
      >
        RUN
      </Button>
    ) : null;
  };

  const onRowExpanded = (e: any) => {
    if (!e) return;

    e.component.collapseAll(-1);
    expandedRowKeysRef.current = [e.key];
  };

  const onRowCollapsed = (e: any) => {
    if (!e) return;
    const key = e.key;
    expandedRowKeysRef.current = (expandedRowKeysRef.current ?? []).filter((k) => k !== key);
  };

  const cellRender = (e: any) => {
    const raw = e?.value ?? e?.data?.lastModified;
    if (!raw) return <div />;

    return <div>{convertUTCTimeToLocal(raw)}</div>;
  };

  const cellRenderSiteName = (e: any) => {
    return (
      <>
        {e?.data?.hqSiteId === 0 ? (
          <div>{e?.data?.hqClientName}</div>
        ) : (
          <div
            style={{
              color: "#009cde",
              textDecoration: "underline",
              cursor: "pointer",
              display: "inline",
            }}
            onClick={() =>
              window.open(
                `${envProperties.reactAppHighQSiteUrl}=${e?.data?.hqSiteId}`
              )
            }
          >
            {e?.data?.hqClientName}
          </div>
        )}
      </>
    );
  };

  return (
    <div style={{ margin: "16px" }}>
      <div
        style={{
          display: "flex",
          margin: "10px 0 10px 0",
        }}
      >
        <h2>Custom Permissions</h2>
        <AppNameToggle showInternalToggle={false} />
      </div>
      <DataGrid
        id="grid-sites"
        ref={dataGridRef}
        keyExpr="hqSiteId"
        allowColumnResizing={true}
        dataSource={customPermissionData?.sites ?? []}
        showBorders={true}
        height={calculateGridHeight(260)}
        remoteOperations={false}
        noDataText={customPermissionData?.pending ? "Loading..." : "No Data"}
        //btnContinueDisable={btnContinueDisable}
        onRowExpanding={onRowExpanded}
        onRowCollapsed={onRowCollapsed}
      >
        <Toolbar>
          <Item key="formContorl" location="after">
            <FormControl size="small" variant="outlined" sx={{ minWidth: 190 }}>
              <InputLabel id="demo-simple-select-label">
                Custom Permissions
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={dropDown}
                style={{ height: 36, fontSize: "14px" }}
                label="Custom Permissions"
                onChange={handleChange}
              >
                {NEW_FLOW_OPTIONS.map((name) => {
                  const isDisabled =
                    appName === AppName.CS &&
                    !CS_NEW_FLOW_OPTIONS.includes(name);

                  return (
                    <MenuItem
                      key={name}
                      value={name}
                      disabled={isDisabled}
                      style={{
                        fontSize: "14px",
                        color: isDisabled ? "#9e9e9e" : "inherit",
                      }}
                    >
                      {name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Item>
          <Item name="searchPanel" location="after" />
          <Item key="refresh" location="after">
            <IconButton
              aria-label="Refresh"
              size="small"
              onClick={handleRefreshPage}
              disabled={isRefreshingPage}
            >
              {isRefreshingPage ? (
                <CircularProgress size={18} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </Item>
        </Toolbar>
        <Selection
          mode="multiple"
          selectAllMode={"page"}
          showCheckBoxesMode={"always"}
        />
        <Scrolling columnRenderingMode="virtual" />
        <FilterRow visible={true} />
        <HeaderFilter visible={true} />
        <SearchPanel
          visible={true}
          width={260}
          placeholder="Search by SiteID or Site Name"
          text={searchTerm}
          onTextChange={(value: string) => setSearchTerm(value || "")}
        />
        <Column
          minWidth={80}
          dataField="hqSiteId"
          caption="SiteID"
          alignment="left"
          allowSearch={true}
        ></Column>
        <Column
          minWidth={80}
          dataField="mdmClientId"
          caption="Master ClientID"
          alignment="left"
          allowSearch={false}
        ></Column>
        <Column
          minWidth={400}
          cellRender={cellRenderSiteName}
          dataField="hqClientName"
          caption="Site Name"
          alignment="left"
          allowSearch={true}
        ></Column>
        <Column
          dataField="isCustomPermissionApplied"
          caption="Custom Permissions"
          width={100}
          alignment="center"
          allowSearch={false}
          cellRender={(rowData) => (
            <CheckBox
              value={!!rowData.data.isCustomPermissionApplied}
              elementAttr={{
                style: "pointer-events: none; opacity: 1;",
              }}
            />
          )}
        />
        <Column
          minWidth={150}
          cellRender={cellRenderForStatus}
          dataField="status"
          caption="Status"
          alignment="left"
          allowSearch={false}
        ></Column>
        <Column
          minWidth={250}
          dataField="lastModifiedBy"
          caption="Last Updated by"
          alignment="left"
          allowSearch={false}
        ></Column>
        <Column
          minWidth={200}
          cellRender={cellRender}
          dataField="lastModified"
          caption="Last Updated Date/Time"
          alignment="left"
          allowSearch={false}
        ></Column>
        <Column
          cellRender={renderActionsColumn}
          type="buttons"
          caption="Actions"
          allowSearch={false}
        ></Column>
        <MasterDetail
          enabled={true}
          ref={templateRef}
          component={CustomPermissionLevelReport}
        ></MasterDetail>
      </DataGrid>
    </div>
  );
}

export default CustomPermissions;
